import { HOST, API_URL, ENV, SSO_LOCAL, SSO_DISABLED } from "@/config";
import { isSSOLoginEnabledForHost } from "./loginConfig";
import { isFreemiumModeEnabled } from "./freemiumMode";

export const FREE_USER_DOMAIN = "app.myaltimate.com";
export const FREE_MEGATENANT = "freemegatenant.demo.tryaltimate.com";
export const MEGA_TENANT = "megatenant";

export const getTenant = () => {
  // 1. Check if tenant is injected by preview server (path-based routing)
  if (window.__TENANT__) {
    return window.__TENANT__;
  }

  // 2. Extract from subdomain (production behavior)
  const myDomain = window.location.host;
  if (!myDomain.includes("." + HOST)) return "";
  return myDomain.replace("." + HOST, "");
};

/**
 * The public-origin host that owns OAuth and instance-subdomain redirects: the
 * build-time `HOST` (`app.myaltimate.com` in prod, `localhost:3000` in dev).
 */
export function publicHost(): string {
  // HOST is `string | undefined` at the config boundary; fall back to the current
  // host (i.e. treat wherever we are as the public origin) if it's ever unset.
  return HOST ?? window.location.host;
}

const CODE_QUERY = "codeQuery";
const LINEAGE = "lineage";
const FREE_STACK = "freeStack";
const IS_INSTANCE = "isInstance";
const SHOW_PREMIUM_DATAPILOT = "showPremiumDatapilot";
const SHOULD_REDIRECT_TO_TEAM_SPENDS = "shouldRedirectToTeamSpends";
const SHOW_ENTERPRISE_DATA_DISCOVERY = "showEnterpriseDataDiscovery";
const SHOW_ENTERPRISE_EDITION_SUGGESTION = "showEnterpriseEditionSuggestion";
const JOBS = "jobs";
const SSO_ENABLED = "ssoEnabled";
const SHOW_WORKFLOW = "showWorkflow";
const featureFlags = {
  [CODE_QUERY]: getTenant() !== "silq",
  [JOBS]: ![
    "shs",
    "block",
    "dat",
    "rampdata",
    "cisco",
    "qaebates",
    "meraki",
    "pepsiaws-us-east",
    "pepsico",
    "rubrik",
  ].includes(getTenant()),
  [LINEAGE]: getTenant() !== "silq",
  [FREE_STACK]:
    window.location.hostname.includes(FREE_USER_DOMAIN) ||
    window.location.hostname.includes(FREE_MEGATENANT),
  [IS_INSTANCE]: window.location.hostname.includes("localhost")
    ? window.location.hostname !== HOST?.split(":")[0]
    : window.location.hostname !== HOST,
  [SHOW_PREMIUM_DATAPILOT]: getTenant() === "eastman",
  [SSO_ENABLED]: [
    "msmg",
    "cisco",
    "eastman",
    "dat",
    "meraki",
    "shs",
    "block",
    "zscaler",
    "zscaler-corp",
    "zscaler-datafabric",
    "zscaler-avalor",
    "docusign",
    "duo",
    "dbtdemo",
    "rakuten",
    "coinbase",
    "azuresso",
    "intuitive",
    "rubrik",
    "pepsico",
    "sunrun",
    "harvestgroup",
    "elastic",
  ].includes(getTenant()),
  [SHOW_ENTERPRISE_DATA_DISCOVERY]: getTenant() === "msmg",
  [SHOW_ENTERPRISE_EDITION_SUGGESTION]: getTenant() !== "msmg",
  [SHOULD_REDIRECT_TO_TEAM_SPENDS]: ["block"].includes(getTenant()),
  [SHOW_WORKFLOW]: getTenant() === "megatenant",
};

export const isCodeQuery = () => featureFlags[CODE_QUERY];
export const isLineage = () => featureFlags[LINEAGE];

// Either the freemium stack itself, or a single tenant flagged into freemium
// mode on the enterprise stack. OR, never override: an absent flag is
// indistinguishable from false once the reducer applies `|| false`, so
// overriding would switch the freemium stack off. See helpers/freemiumMode.ts.
export const isFreeStack = () => featureFlags[FREE_STACK] || isFreemiumModeEnabled();
export const showPremiumDatapilot = () => featureFlags[SHOW_PREMIUM_DATAPILOT];
export const showJobs = () => featureFlags[JOBS];
export const isInstanceStack = () => featureFlags[IS_INSTANCE];
export const getDefaultRoute = (tenant: string) => (tenant === "silq" ? "/datasets" : "/summary");
export const getBackendUrl = () => {
  return API_URL;
};
export const isSSOEnabled = () => {
  if (SSO_DISABLED) {
    return false;
  }
  // Temporary release-cert recovery: keep the approved password test account
  // usable without changing production SSO or shared tenant settings.
  // Do not carry this exception into a production release branch.
  if (
    [
      "https://rakuten.app-release-cert.getaltimate.com",
      "https://cisco.app-release-cert.getaltimate.com",
      "https://shs.app-release-cert.getaltimate.com",
      "https://pepsico.app-release-cert.getaltimate.com",
      "https://intuitive.app-release-cert.getaltimate.com",
    ].includes(window.location.origin) &&
    HOST === "app-release-cert.getaltimate.com" &&
    API_URL === "https://api-release-cert.getaltimate.com"
  ) {
    return false;
  }

  // Two independent ways this host gets the SSO login experience, OR'd:
  //   1. `ssoLoginEnabled` — the server-driven flag, hydrated pre-render from
  //      the public GET /auth/login-config (see loginConfig.ts). Answered per
  //      HOST, not per tenant: SSO providers are registered against the
  //      subdomain, so a tenant reachable at two subdomains can have SSO on one
  //      and password login on the other. Turning it on is guarded backend-side
  //      on that host having an IdP, so `true` means SSO is genuinely usable.
  //   2. SSO_ENABLED — the baked-in allowlist below, which predates the flag
  //      and is likewise matched on `getTenant()`, i.e. per host.
  // The flag is additive: it onboards a host to SSO without a frontend deploy,
  // and can never take SSO away from an already-allowlisted one.
  const ssoLoginActive = isSSOLoginEnabledForHost() || featureFlags[SSO_ENABLED];

  // Local hosts stay off unless the machine opts in: without a SuperTokens
  // core, a provisioned tenant and a registered redirect URI the SSO login page
  // dead-ends instead of falling back to the password form. With
  // PUBLIC_SSO_LOCAL=true the tenant flag then drives the mode, so switching
  // between the password form and SSO is a PATCH + reload, with no rebuild.
  if (
    ENV === "local" &&
    (window.location.hostname.includes("localhost") ||
      window.location.hostname.includes("3000.app.github.dev"))
  ) {
    return SSO_LOCAL && ssoLoginActive;
  }

  return ssoLoginActive;
};
export const showEnterpriseDataDiscovery = () => featureFlags[SHOW_ENTERPRISE_DATA_DISCOVERY];
export const showEnterpriseEditionSuggestion = () =>
  featureFlags[SHOW_ENTERPRISE_EDITION_SUGGESTION];
export const shouldRedirectToTeamSpends = () => featureFlags[SHOULD_REDIRECT_TO_TEAM_SPENDS];
export const showWorkflow = () => featureFlags[SHOW_WORKFLOW];
export const isMegaTenant = () => getTenant() === MEGA_TENANT;

/**
 * The tenantless enterprise host — the public-admin / internal dashboard
 * (app.getaltimate.com with no tenant sub-domain). It has no tenant schema, so
 * tenant-scoped requests (e.g. `/auth/tenant-info`) are rejected for the
 * public tenant and must not be sent from it. The bare free-stack host is also
 * tenantless but is deliberately NOT this host: its tenant-info resolves
 * normally, hence the free-stack carve-out.
 *
 * This is the single definition of that host. AppProvider's bootstrap gate
 * layers a second, user-based signal on top of the missing sub-domain (the
 * stored login's `permission === null`) because jsdom tests run tenantless and
 * would otherwise all match — the free-stack carve-out itself lives only here.
 */
export const isTenantlessEnterpriseHost = () => getTenant() === "" && !isFreeStack();
